
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { ArrowRight, Download, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

// Define interface for the press release objects
interface PressRelease {
  id: string;
  date: string;
  title: string;
  description: string;
  link: string;
}

// Define interface for the kit items
interface KitItem {
  [index: number]: string;
}

const PressPage = () => {
  const { t } = useTranslation("common");

  // Cast the returned translations to the appropriate types
  const releases = t("press.releases", { returnObjects: true }) as PressRelease[];
  const kitItems = t("press.kitItems", { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>{t("press.pageTitle")}</title>
        <meta name="description" content={t("press.subtitle")} />
        <meta property="og:title" content={t("press.title")} />
        <meta property="og:description" content={t("press.subtitle")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "headline": t("press.title"),
            "description": t("press.subtitle"),
            "publisher": {
              "@type": "Organization",
              "name": "Verdea",
              "logo": {
                "@type": "ImageObject",
                "url": window.location.origin + "/og-image.png"
              }
            }
          })}
        </script>
      </Helmet>
      <NavBar />
      <main className="pt-24">
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
              <div className="space-y-2">
                <h1 className="text-4xl md:text-6xl font-semibold leading-tight">
                  {t("press.title")}
                </h1>
                <p className="text-2xl md:text-3xl text-gray-600 font-semibold">
                  {t("press.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Press Releases Section */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-semibold mb-8">{t("press.latestReleases")}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {releases.map((release, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="text-sm text-gray-500 mb-2">{release.date}</div>
                    <h3 className="text-xl font-bold mb-3">{release.title}</h3>
                    <p className="text-gray-600 mb-4">{release.description}</p>
                    <Link 
                      to={`/press/${release.id}`} 
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {release.link} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Media Kit Section */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-semibold mb-4">{t("press.mediaKit")}</h2>
                <p className="text-lg text-gray-600 mb-4">{t("press.kitIncludes")}</p>
                <ul className="space-y-2 mb-6">
                  {kitItems.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="rounded-full bg-green-100 p-1 mr-3 mt-1">
                        <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Button className="inline-flex items-center bg-blue-600 hover:bg-blue-700">
                  <Download className="mr-2 h-4 w-4" /> {t("press.downloadKit")}
                </Button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">{t("press.contactTitle")}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">{t("press.contactName")}</p>
                    <p className="text-gray-600">{t("press.contactPosition")}</p>
                  </div>
                  <a 
                    href={`mailto:${t("press.contactEmail")}`} 
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {t("press.contactEmail")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PressPage;
